import { LocaleTextHook } from '@moonpig/web-core-locale-text'
import { DepartmentType, filterItemsByActiveDepartment } from '../departmentMap'
import {
  InspirationItemType,
  InspirationRow,
  InspirationType,
  RecentRow,
  RecentSearchItemType,
  RecentSearchType,
  ReminderItemType,
  ReminderRow,
} from '../types'
import { FocusableSectionContext } from './types'
import { rowId, staticRowId } from './utils'
import type { Reminder } from '../Reminders/Reminder'
import { NAV_TEXT } from '../../locale'
import { MAX_RECENT_SEARCHES } from '../constants'

export const buildRecentSearchesSection = ({
  focusableSectionId,
  focusableRows,
  activeDepartment,
  recentSearches,
  startIndex,
  staticRowIndex,
  localiseText,
}: FocusableSectionContext & {
  activeDepartment: DepartmentType
  recentSearches: RecentSearchType[]
  startIndex: number
  staticRowIndex: number
  localiseText: LocaleTextHook<typeof NAV_TEXT>
}) => {
  let index = startIndex
  let currentStaticIndex = staticRowIndex

  const activeDeptRecentSearches = filterItemsByActiveDepartment({
    activeDepartment,
    items: recentSearches,
    limit: MAX_RECENT_SEARCHES,
  })

  if (activeDeptRecentSearches.length) {
    focusableRows.push({
      id: staticRowId(currentStaticIndex),
      kind: 'header',
      sectionId: focusableSectionId,
      title: localiseText('search.recently_searched'),
    })
    currentStaticIndex += 1

    activeDeptRecentSearches.forEach(recent => {
      const item: RecentSearchItemType = {
        ...recent,
        department:
          activeDepartment.name === 'all' ? recent.department : undefined,
        itemType: 'recent',
      }
      index += 1
      const row: RecentRow = {
        id: rowId(index),
        kind: 'recent',
        index,
        item,
        sectionId: focusableSectionId,
      }

      focusableRows.push(row)
    })
  }

  return { index, staticRowIndex: currentStaticIndex }
}

export const buildRemindersSection = ({
  focusableSectionId,
  focusableRows,
  reminders,
  localiseText,
  startIndex,
  staticRowIndex,
}: FocusableSectionContext & {
  reminders: Reminder[]
  localiseText: LocaleTextHook<typeof NAV_TEXT>
  startIndex: number
  staticRowIndex: number
}) => {
  let index = startIndex
  let currentStaticIndex = staticRowIndex

  if (reminders.length) {
    focusableRows.push({
      id: staticRowId(currentStaticIndex),
      kind: 'header',
      sectionId: focusableSectionId,
      title: localiseText('search.upcoming_occasions'),
    })
    currentStaticIndex += 1

    reminders.forEach(reminder => {
      const item: ReminderItemType = {
        itemType: 'reminder',
        id: reminder.id,
        value: reminder.value,
        occasion: reminder.occasion,
        date: reminder.date,
      }
      index += 1
      const row: ReminderRow = {
        id: rowId(index),
        kind: 'reminder',
        index,
        item,
        sectionId: focusableSectionId,
      }

      focusableRows.push(row)
    })
  }

  return { index, staticRowIndex: currentStaticIndex }
}

export const buildInspirationsSection = ({
  focusableSectionId,
  focusableRows,
  activeDepartment,
  inspirations,
  localiseText,
  startIndex,
  staticRowIndex,
}: FocusableSectionContext & {
  activeDepartment: DepartmentType
  inspirations: InspirationType[]
  localiseText: LocaleTextHook<typeof NAV_TEXT>
  startIndex: number
  staticRowIndex: number
}) => {
  let index = startIndex
  let currentStaticIndex = staticRowIndex

  const activeDeptInspirations = filterItemsByActiveDepartment({
    activeDepartment,
    items: inspirations,
  })

  if (activeDeptInspirations.length) {
    focusableRows.push({
      id: staticRowId(currentStaticIndex),
      kind: 'header',
      sectionId: focusableSectionId,
      title: localiseText('search.trending'),
    })
    currentStaticIndex += 1

    activeDeptInspirations.forEach(insp => {
      const item: InspirationItemType = {
        itemType: 'inspiration',
        value: insp.value,
        department: insp.department,
        searchTerm: insp.searchTerm,
      }
      index += 1
      const row: InspirationRow = {
        id: rowId(index),
        kind: 'inspiration',
        index,
        item,
        sectionId: focusableSectionId,
      }

      focusableRows.push(row)
    })
  }

  return { index, staticRowIndex: currentStaticIndex }
}
